import { ChevronRight } from "lucide-react";
import { useId, useState } from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface CollapsibleCardProps {
  /** The one line always on screen. Doubles as the collapsed state's whole UI. */
  title: React.ReactNode;
  children: React.ReactNode;
  /** Open on first render. Uncontrolled unless `open` is supplied. */
  defaultOpen?: boolean;
  /** Controlled open state. Pair with `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Muted text on the right of the header — a count, a total, a hint. Hidden
   *  from the accessible name so the toggle still announces as just the title. */
  meta?: React.ReactNode;
  /** Renders without the Card chrome, for nesting inside an existing panel. */
  bare?: boolean;
  className?: string;
  contentClassName?: string;
}

/**
 * A titled block that collapses to a single clickable row.
 *
 * Built for reference content that earns its place on the page but shouldn't
 * cost a screenful every visit — the reconciliation steps' "What this step
 * does" panel is the case that prompted it: genuinely useful the first few
 * times you work a step, pure scroll tax by the tenth, and it was pushing the
 * actual worklist below the fold on every one of 21 steps.
 *
 * The chevron rotates rather than swapping icons, so the control reads as one
 * thing in two states instead of two different buttons.
 *
 * Use a Card with a plain heading when the content is the point of the page.
 * Use this when the content is context *for* the point of the page.
 */
export function CollapsibleCard({
  title,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  meta,
  bare = false,
  className,
  contentClassName,
}: CollapsibleCardProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolled;
  const contentId = useId();

  function toggle() {
    if (!isControlled) setUncontrolled((v) => !v);
    onOpenChange?.(!isOpen);
  }

  const header = (
    <button
      type="button"
      onClick={toggle}
      aria-expanded={isOpen}
      aria-controls={contentId}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-4 py-3 text-left",
        "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-ring focus-visible:ring-offset-1",
      )}
    >
      <ChevronRight
        aria-hidden
        className={cn(
          "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150",
          isOpen && "rotate-90",
        )}
      />
      <span className="text-base font-semibold">{title}</span>
      {meta != null && (
        <span aria-hidden className="ml-auto text-xs text-muted-foreground">
          {meta}
        </span>
      )}
    </button>
  );

  const body = (
    <>
      {header}
      {/* Unmounted rather than hidden: the reconciliation prose is long, and
          keeping 21 steps' worth in the tree buys nothing. */}
      {isOpen && (
        <div id={contentId} className={cn("space-y-4 px-4 pb-4", contentClassName)}>
          {children}
        </div>
      )}
    </>
  );

  if (bare) return <div className={className}>{body}</div>;
  return <Card className={cn("overflow-hidden", className)}>{body}</Card>;
}
